import { CarrierVisitStatus, OrderStatus } from '@planning/app/api'
import { VesselVisitOperation } from '@planning/pages/TallymanV2/Components/VesselSelectionStepper'
import locationCodeService from '@planning/services/locationCodeService'
import _ from 'lodash'
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx'
import { IEntityStore } from '../types'
import { IVesselVisitItem } from '../vesselVisit/VesselVisitItem'
import { CheckOrderNotificationStore } from './CheckOrderNotificationStore'
import { ReportContainerNotificationStore } from './ReportContainerNotificationStore'
import { SearchContainerViewStore } from './SearchContainerViewStore'
import { SearchRobViewStore } from './SearchRobViewStore'

export interface ICrane {
  id: number
  name: string
}
export interface IAssignedCraneForOrder {
  craneId?: number | null
  craneName?: string | null
  orderId?: number | null
}
export interface IVesselTallyViewStore {
  vesselVisits: IVesselVisitItem[]
}
export class VesselTallyViewStore implements IVesselTallyViewStore {
  selectedVisitId?: number
  selectedOperation?: VesselVisitOperation

  cranes: ICrane[] = []
  selectedCrane?: ICrane
  assignedCraneForOrder?: IAssignedCraneForOrder[]

  searchContainerStore: SearchContainerViewStore
  checkNotificationStore: CheckOrderNotificationStore
  reportNotificationStore: ReportContainerNotificationStore
  searchRobStore: SearchRobViewStore
  locationCodeNames: { [key: string]: string } = {}
  private searchedLocationCodeNames: string[] = []

  constructor(private readonly vesselVisitItemStore: IEntityStore<IVesselVisitItem>) {
    makeObservable(this, {
      selectedVisitId: observable,
      selectedOperation: observable,
      cranes: observable,
      selectedCrane: observable,
      locationCodeNames: observable,

      vesselVisits: computed,
      orders: computed,
      selectedVisit: computed,
      isSelectedVisitInOperation: computed,
      loadRestowsWithInboundFinished: computed,
      totalDischargeOrders: computed,
      totalLoadOrders: computed,
      totalDischargeConfirmed: computed,
      totalLoadConfirmed: computed,
      locationCodes: computed,

      selectVesselVisit: action,
      selectOperation: action,
      setCranes: action,
      selectCrane: action,
      setAssignedCraneForOrder: action,
    })

    this.searchContainerStore = new SearchContainerViewStore(this)
    this.checkNotificationStore = new CheckOrderNotificationStore()
    this.reportNotificationStore = new ReportContainerNotificationStore()
    this.searchRobStore = new SearchRobViewStore(this)

    reaction(
      () => this.locationCodes,
      codes => this.getLocationCodeNames(codes),
    )
  }

  get vesselVisits() {
    return _(this.vesselVisitItemStore.elements)
      .filter(vv => vv.data.status === CarrierVisitStatus.InOperation)
      .value()
  }

  get loadRestowsWithInboundFinished() {
    return (
      this.selectedVisit?.restow?.loadOrders.filter(
        x => x.containerJourney?.inboundOrderStatus === OrderStatus.Fulfilled,
      ) ?? []
    )
  }

  get orders() {
    if (this.selectedOperation === 'Discharge') {
      return _.union(
        this.selectedVisit?.discharge.orders,
        this.selectedVisit?.restow?.dischargeOrders ?? [],
      )
    } else {
      return _.union(this.selectedVisit?.load.orders, this.loadRestowsWithInboundFinished)
    }
  }

  get selectedVisit() {
    return _(this.vesselVisitItemStore.elements).find(vv => vv.id === this.selectedVisitId)
  }

  get isSelectedVisitInOperation() {
    return this.selectedVisit?.data.status === CarrierVisitStatus.InOperation
  }

  public get totalDischargeOrders() {
    return (
      Number(this.selectedVisit?.discharge?.orderCounts?.container?.total || 0) +
      Number(this.selectedVisit?.restow?.orderCounts?.discharge?.total || 0)
    )
  }

  public get totalLoadOrders() {
    return (
      Number(this.selectedVisit?.load?.orderCounts?.container?.total || 0) +
      (this.loadRestowsWithInboundFinished?.length || 0)
    )
  }

  public get totalDischargeConfirmed() {
    return (
      Number(this.selectedVisit?.discharge?.orderCounts?.container?.confirmed || 0) +
      Number(this.selectedVisit?.restow?.orderCounts?.discharge?.confirmed || 0)
    )
  }

  public get totalLoadConfirmed() {
    return (
      Number(this.selectedVisit?.load?.orderCounts?.container?.confirmed || 0) +
      Number(this.selectedVisit?.restow?.orderCounts?.load?.confirmed || 0)
    )
  }

  public get locationCodes() {
    const podCodes = this.orders
      .map(order => order.data.portOfDischarge)
      .filter((code): code is string => !!code)

    const polCodes = this.orders
      .map(order => order.data.portOfLoading)
      .filter((code): code is string => !!code)

    return _.uniq([...podCodes, ...polCodes])
  }

  selectVesselVisit = (id?: number) => {
    this.selectedVisitId = id
  }

  selectOperation = (operation: VesselVisitOperation) => {
    this.selectedOperation = operation
  }

  selectCrane = (crane?: ICrane) => {
    this.selectedCrane = crane
  }

  setAssignedCraneForOrder = (assignedCraneForOrder: IAssignedCraneForOrder[]) => {
    this.assignedCraneForOrder = assignedCraneForOrder
  }

  setCranes = (cranes: ICrane[]) => {
    this.cranes = _(cranes)
      .sortBy(c => c.name)
      .value()
  }

  public getLocationNameByCode(code?: string | null): string | undefined {
    if (!code) return undefined

    const name = this.locationCodeNames[code]

    return name ? `${code} - ${name}` : code
  }

  private async getLocationCodeNames(locationCodes: string[]) {
    const codesToFetch = locationCodes.filter(
      code => !(code in this.locationCodeNames) && !this.searchedLocationCodeNames.includes(code),
    )

    if (codesToFetch.length === 0) return
    this.searchedLocationCodeNames = [...this.searchedLocationCodeNames, ...codesToFetch]

    const response = await locationCodeService.getNamesByCodes(codesToFetch)

    runInAction(() => {
      this.locationCodeNames = { ...this.locationCodeNames, ...response }
    })
  }
}
